import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Undo2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/pages/Leads";
import { toast } from "sonner";

const TONE = { "Pending Approval": "secondary", Approved: "default", Rejected: "destructive" };

export default function InventoryCorrections({ onChanged }) {
  const [data, setData] = useState({ corrections: [], can_approve: false });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [form, setForm] = useState({ corrected_quantity: "", remark: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/inventory/corrections");
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (decision) => {
    try {
      await api.post(`/inventory/corrections/${acting.id}/decision`, {
        decision,
        remark: form.remark,
        corrected_quantity:
          decision === "approve" && form.corrected_quantity !== ""
            ? Number(form.corrected_quantity)
            : undefined,
      });
      toast.success(
        decision === "approve"
          ? "Correction approved — audit-safe adjustment posted, original entry kept"
          : "Correction rejected"
      );
      setActing(null);
      setForm({ corrected_quantity: "", remark: "" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="inventory-corrections">
      <p className="text-[11px] text-muted-foreground">
        Posted transactions are never edited or deleted. A correction is approved by the Owner or a
        Director and posted as a tracked adjustment with full history.
      </p>
      <div className="surface overflow-x-auto" data-testid="corrections-table">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pl-3">Item</th>
              <th className="py-2 pr-2">Original</th>
              <th className="py-2 pr-2">Requested</th>
              <th className="py-2 pr-2">Reason</th>
              <th className="py-2 pr-2">Requested by</th>
              <th className="py-2 pr-2">Approved by</th>
              <th className="py-2 pr-3 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.corrections.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0"
                data-testid={`correction-row-${c.id}`}>
                <td className="py-2 pl-3">{c.item_name}</td>
                <td className="py-2 pr-2 font-mono-num">
                  {c.original_quantity} {c.unit}
                  <div className="text-[10px] text-muted-foreground">{c.original_kind}</div>
                </td>
                <td className="py-2 pr-2 font-mono-num">
                  {c.corrected_quantity == null ? "—" : `${c.corrected_quantity} ${c.unit || ""}`}
                </td>
                <td className="max-w-[16rem] py-2 pr-2">
                  {c.reason}
                  {c.remarks && <div className="text-[10px] text-muted-foreground">{c.remarks}</div>}
                </td>
                <td className="py-2 pr-2">
                  {c.requested_by_name}
                  <div className="text-[10px] text-muted-foreground">
                    {String(c.requested_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                </td>
                <td className="py-2 pr-2">
                  {c.approved_by_name || "—"}
                  {c.approved_at && (
                    <div className="text-[10px] text-muted-foreground">
                      {String(c.approved_at).slice(0, 16).replace("T", " ")}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-3 text-right">
                  <Badge variant={TONE[c.status] || "outline"} className="text-[9px]">{c.status}</Badge>
                  {data.can_approve && c.status === "Pending Approval" && (
                    <Button size="sm" variant="outline" className="ml-2 h-6 gap-1 px-2 text-[10px]"
                      data-testid={`correction-review-${c.id}`}
                      onClick={() => {
                        setActing(c);
                        setForm({
                          corrected_quantity: c.corrected_quantity ?? "",
                          remark: "",
                        });
                      }}>
                      <Undo2 className="h-3 w-3" /> Review
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {data.corrections.length === 0 && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-muted-foreground">
                  No correction requests.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!acting} onOpenChange={(o) => !o && setActing(null)}>
        <DialogContent data-testid="correction-decision-dialog">
          <DialogHeader>
            <DialogTitle>Review correction — {acting?.item_name}</DialogTitle>
          </DialogHeader>
          <div className="rounded border border-border bg-secondary/40 p-3 text-xs">
            <div>Original {acting?.original_kind}: {acting?.original_quantity} {acting?.unit}</div>
            <div className="mt-1">Reason: {acting?.reason}</div>
            <div className="mt-1 text-muted-foreground">
              Requested by {acting?.requested_by_name}
            </div>
          </div>
          <Field label={`Correct quantity (${acting?.unit || "unit"})`}>
            <Input type="number" data-testid="correction-quantity-input"
              value={form.corrected_quantity}
              onChange={(e) => setForm({ ...form, corrected_quantity: e.target.value })} />
          </Field>
          <Field label="Approval remark">
            <Input data-testid="correction-remark-input" value={form.remark}
              onChange={(e) => setForm({ ...form, remark: e.target.value })} />
          </Field>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="gap-1" data-testid="correction-reject-button"
              onClick={() => decide("reject")}>
              <X className="h-4 w-4" /> Reject
            </Button>
            <Button className="gap-1" data-testid="correction-approve-button"
              onClick={() => decide("approve")}>
              <Check className="h-4 w-4" /> Approve & post adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
